import AddIcon from "@mui/icons-material/Add";
import { Box, Card, CardContent, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminLanguage } from "../../../context/AdminLanguageContext";
import { ADMIN_SLIDING_BROWSE_PATH } from "../../story/adminSlidingRoutes";

const STATE_ORDER = ["DRAFT", "READY_TO_REVIEW", "REVIEWED", "PUBLISHED"];

const getCellValue = (byState, state) => (byState && byState[state]) ?? 0;

function buildNewHref(row, linkToAdminBoards) {
    if (linkToAdminBoards) {
        if (row.kind === "grid") {
            return "/admin/puzzle/grid_board";
        }
        if (row.kind === "sliding") {
            return ADMIN_SLIDING_BROWSE_PATH;
        }
        if (row.kind === "jigsaw") {
            return "/admin/jigsaw/browse";
        }
        if (row.kind === "word_wheel") {
            return "/admin/puzzle/word_wheel_board";
        }
        if (row.kind === "standalone_image") {
            return "/admin/story";
        }
        return "/admin/puzzle/cross_board";
    }
    const lang = row.language;
    if (row.kind === "grid") {
        return `/creator/grid/edit?language=${lang}&type=${row.gridType}`;
    }
    return `/creator/crossword/edit?language=${lang}`;
}

const TYPE_GROUPS = [
    { typeLabel: "Crossword", kind: "cross", byStateKey: "crossByState" },
    { typeLabel: "Hidden Message", gridType: "HIDDEN_MESSAGE", kind: "grid", byStateKey: "gridHiddenMessageByState" },
    { typeLabel: "Word Search", gridType: "WORD_SEARCH", kind: "grid", byStateKey: "gridWordSearchByState" },
    { typeLabel: "Word Wheel", kind: "word_wheel", byStateKey: "wordWheelByState" },
    { typeLabel: "Sliding", kind: "sliding", byStateKey: "slidingByState" },
    { typeLabel: "Jigsaw", kind: "jigsaw", byStateKey: "jigsawByState" },
    { typeLabel: "Standalone Image", kind: "standalone_image", byStateKey: "standaloneImageByState" },
];

const DEFAULT_TITLE = "Statistics by state";

/** Grid/crossword/word wheel rows open the admin board from +; sliding/jigsaw/standalone use browse flows. */
function rowShowsNewAction(row) {
    return row.kind !== "sliding" && row.kind !== "jigsaw" && row.kind !== "standalone_image";
}

export default function StatsByStateTable({ englishStats, frenchStats, title, contentLanguage, linkToAdminBoards, hideCrossword = false, hideSumRow = false, hideFrench = false }) {
    const navigate = useNavigate();
    const { setLanguage } = useAdminLanguage();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const eng = englishStats || {};
    const fr = frenchStats || {};
    const displayTitle = title != null ? title : DEFAULT_TITLE;
    const showSingleLanguage = contentLanguage === "english" || contentLanguage === "french";
    const groups = (() => {
        const g = [];
        const typeGroups = hideCrossword ? TYPE_GROUPS.filter((tg) => tg.kind !== "cross") : TYPE_GROUPS;
        for (const tg of typeGroups) {
            const enByState = tg.byStateKey ? eng[tg.byStateKey] : null;
            const frByState = tg.byStateKey ? fr[tg.byStateKey] : null;
            const hasEn = enByState && Object.keys(enByState).length > 0;
            const hasFr = frByState && Object.keys(frByState).length > 0;
            if (!hasEn && !hasFr) continue;
            const rows = [];
            if (!showSingleLanguage) {
                if (hasEn) rows.push({ lang: "English", language: "english", byState: enByState, kind: tg.kind, gridType: tg.gridType });
                if (!hideFrench && hasFr) rows.push({ lang: "French", language: "french", byState: frByState, kind: tg.kind, gridType: tg.gridType });
            } else {
                if (contentLanguage === "english" && hasEn) rows.push({ lang: "EN", language: "english", byState: enByState, kind: tg.kind, gridType: tg.gridType });
                if (contentLanguage === "french" && hasFr) rows.push({ lang: "FR", language: "french", byState: frByState, kind: tg.kind, gridType: tg.gridType });
            }
            if (rows.length > 0) g.push({ typeLabel: tg.typeLabel, rows });
        }
        return g;
    })();

    /** Admin home → board routes: align `admin.content.lang` and AdminLanguageContext with the row before navigate. */
    const handleNewNavigate = useCallback(
        (row) => {
            if (linkToAdminBoards) {
                setLanguage(row.language);
            }
            navigate(buildNewHref(row, linkToAdminBoards));
        },
        [linkToAdminBoards, navigate, setLanguage]
    );

    if (groups.length === 0) return null;

    // Sum per state across all rows
    const sumByState = STATE_ORDER.reduce((acc, state) => {
        acc[state] = 0;
        return acc;
    }, {});
    groups.forEach((group) => {
        group.rows.forEach((row) => {
            if (row.kind === "standalone_image") {
                return;
            }
            STATE_ORDER.forEach((state) => {
                sumByState[state] += getCellValue(row.byState, state);
            });
        });
    });

    if (isMobile) {
        return (
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                        {displayTitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Puzzle counts by workflow state{showSingleLanguage ? "" : " per language"}.
                    {hideCrossword
                        ? " Tap + on grid and word wheel rows to open the board."
                        : " Tap + on crossword, grid, and word wheel rows to open the board."}
                    {" "}
                    Standalone Image packs are included in Sliding and Jigsaw; that row is a subset and is not added again in Sum.
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {groups.map((group) =>
                            group.rows.map((row) => (
                                <Box
                                    key={`${group.typeLabel}-${row.lang}`}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        flexWrap: "wrap",
                                        gap: 1,
                                        py: 1,
                                        px: 1.5,
                                        borderRadius: 1,
                                        bgcolor: "grey.50",
                                        border: "1px solid",
                                        borderColor: "divider"
                                    }}
                                >
                                    <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            {group.typeLabel}{!showSingleLanguage ? ` · ${row.lang}` : ""}
                                        </Typography>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, mt: 0.5 }}>
                                            {STATE_ORDER.map((state) => {
                                                const value = getCellValue(row.byState, state);
                                                return (
                                                    <Box key={state} sx={{ display: "grid", gridTemplateColumns: "4fr 4fr 4fr", gap: 1, alignItems: "center", minWidth: 0 }}>
                                                        <Typography variant="caption" sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                            {state.replace(/_/g, " ")}
                                                        </Typography>
                                                        <Box /> {/* spacer */}
                                                        <Typography variant="caption" sx={{ color: value === 0 ? "error.main" : "text.secondary", fontWeight: value === 0 ? 600 : 500, textAlign: "right" }}>
                                                            {value}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                    {rowShowsNewAction(row) ? (
                                        <Tooltip title="New (create draft and open editor)">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleNewNavigate(row)}
                                                aria-label={`New ${group.typeLabel} ${row.lang}`}
                                                sx={{ border: "1px solid", borderColor: "primary.main", mt: 0.5, mb: 0.5, ml: 2 }}
                                            >
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : null}
                                </Box>
                            ))
                        )}
                        {!hideSumRow && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    py: 1.5,
                                    px: 1.5,
                                    borderRadius: 1,
                                    border: "1px solid",
                                    borderColor: "divider"
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    Sum
                                </Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, mt: 0.5 }}>
                                    {STATE_ORDER.map((state) => (
                                        <Box key={state} sx={{ display: "grid", gridTemplateColumns: "4fr 4fr 4fr", gap: 1, alignItems: "center", minWidth: 0 }}>
                                            <Typography variant="caption" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{state.replace(/_/g, " ")}</Typography>
                                            <Box /> {/* spacer */}
                                            <Typography variant="caption" sx={{ fontWeight: 600, textAlign: "right" }}>
                                                {sumByState[state]}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                    {displayTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Puzzle counts by workflow state{showSingleLanguage ? "" : " per language"}.
                    {!hideCrossword
                        ? " Use the New icon on crossword, grid, and word wheel rows to open the board."
                        : " Use the New icon on grid and word wheel rows to open the board."}
                    {" "}
                    Standalone Image packs are included in Sliding and Jigsaw; that row is a subset and is not added again in Sum.
                </Typography>
                <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <Table size="small" stickyHeader sx={{ minWidth: 520 }}>
                        <TableHead>
                            <TableRow sx={{ '& .MuiTableCell-root': { py: 2 } }}>
                                <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Type</TableCell>
                                {!showSingleLanguage && (
                                    <TableCell sx={{ fontWeight: 600, width: 48 }}>Language</TableCell>
                                )}
                                {STATE_ORDER.map((state) => (
                                    <TableCell key={state} align="right" sx={{ fontWeight: 600 }}>
                                        {state.replace(/_/g, " ")}
                                    </TableCell>
                                ))}
                                <TableCell sx={{ fontWeight: 600, width: 72 }} align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groups.map((group) =>
                                group.rows.map((row, idx) => (
                                    <TableRow key={`${group.typeLabel}-${row.lang}`} sx={{ '& .MuiTableCell-root': { py: 2 } }}>
                                        {idx === 0 ? (
                                            <TableCell
                                                rowSpan={showSingleLanguage ? 1 : group.rows.length}
                                                sx={{ verticalAlign: "middle", fontWeight: 600 }}
                                            >
                                                {group.typeLabel}
                                            </TableCell>
                                        ) : null}
                                        {!showSingleLanguage && <TableCell>{row.lang}</TableCell>}
                                        {STATE_ORDER.map((state) => {
                                            const value = getCellValue(row.byState, state);
                                            const isZero = value === 0;
                                            return (
                                                <TableCell key={state} align="right" sx={isZero ? { color: "error.main", "& > span": { fontSize: "1.1rem", fontWeight: 600 } } : undefined}>
                                                    <span>{value}</span>
                                                </TableCell>
                                            );
                                        })}
                                        <TableCell align="center" sx={{ py: 2, px: 1 }}>
                                            {rowShowsNewAction(row) ? (
                                                <Tooltip title="New (create draft and open editor)">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleNewNavigate(row)}
                                                        aria-label={`New ${group.typeLabel} ${row.lang}`}
                                                        sx={{ border: "1px solid", borderColor: "primary.main", margin: "6px" }}
                                                    >
                                                        <AddIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                            {!hideSumRow && (
                                <TableRow sx={{ '& .MuiTableCell-root': { py: 2 } }}>
                                    <TableCell colSpan={showSingleLanguage ? 1 : 2} sx={{ fontWeight: 700 }}>
                                        Sum
                                    </TableCell>
                                    {STATE_ORDER.map((state) => (
                                        <TableCell key={state} align="right" sx={{ fontWeight: 600 }}>
                                            {sumByState[state]}
                                        </TableCell>
                                    ))}
                                    <TableCell align="center" />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
}
